import React from 'react';
import { Modal, Row, Col, Button } from 'antd';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { InputField, SelectField, RadioGroupField } from '@/components/Form';
import GoogleCustomLocationField from '@/components/Elements/GoogleCustomLocation';
import {
  PROPERTY_LENDER_OPTIONS,
  PROPERTY_USE_TYPE_OPTIONS,
  TAX_PROPERTY_OWNER_TYPE_OPTIONS,
  FREQUENCY_OPTIONS,
} from '@/constant/options';
import { usePostPersonalPropertyMutation } from '@/pages/mortgage/apis/propertyAPI';
import { displaySuccess, displayError } from '@/utils/displayMessageUtils';

const YES_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

type AddressObject = {
  apartment: string;
  streetNumber: string;
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  fullAddress?: string;
};

type FormValues = {
  propertyOwner: string;
  propertyAddress: string;
  /**
   * Last address the user picked out of the Google search results. Kept in the
   * form purely so `propertyAddress` can be validated against it — free text
   * that was never selected must not be stored.
   */
  selectedAddress: string;
  propertyAddressObject: AddressObject | null;
  propertyValue: string;
  propertyUse: string;
  propertyRentFrequency: string;
  propertyRent: string;
  isMortgageOnProperty: boolean | null;
  mortgageLender: string;
  mortgageLoanLimit: string;
  mortgageInterest: string;
};

// "Australia", "australia,", "AU" etc. on their own are not an address — Google
// Places will happily return the bare country if the user stops typing early.
const isCountryOnlyAddress = (value: string) => {
  const letters = value.toLowerCase().replace(/[^a-z]/g, '');
  return letters === 'australia' || letters === 'au';
};

const addPropertySchema: yup.ObjectSchema<FormValues> = yup.object({
  propertyOwner: yup.string().required('Property owner is required'),
  propertyAddress: yup
    .string()
    .trim()
    .required('Property address is required')
    .test(
      'not-country-only',
      'Enter the full property address, not just the country',
      (value) => !isCountryOnlyAddress(value ?? ''),
    )
    .test(
      'picked-from-search',
      'Select the property address from the search results',
      (value, ctx) =>
        (value ?? '') === ((ctx.parent.selectedAddress as string) ?? '').trim(),
    ),
  selectedAddress: yup.string().defined(),
  propertyAddressObject: yup.mixed<AddressObject>().nullable().defined(),
  propertyValue: yup.string().required('Property value is required'),
  propertyUse: yup.string().defined(),
  propertyRentFrequency: yup.string().defined(),
  propertyRent: yup.string().defined(),
  isMortgageOnProperty: yup.boolean().nullable().defined(),
  mortgageLender: yup.string().defined(),
  mortgageLoanLimit: yup.string().defined(),
  mortgageInterest: yup.string().defined(),
});

const DEFAULTS: FormValues = {
  propertyOwner: '',
  propertyAddress: '',
  selectedAddress: '',
  propertyAddressObject: null,
  propertyValue: '',
  propertyUse: '',
  propertyRentFrequency: '',
  propertyRent: '',
  isMortgageOnProperty: null,
  mortgageLender: '',
  mortgageLoanLimit: '',
  mortgageInterest: '',
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const AddPropertyModal: React.FC<Props> = ({ open, onClose }) => {
  const [postProperty, { isLoading }] = usePostPersonalPropertyMutation();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: DEFAULTS,
    resolver: yupResolver(addPropertySchema),
  });

  const isMortgaged = watch('isMortgageOnProperty');

  const handleSelectedAddressChange = (address: string | null) => {
    setValue('selectedAddress', address ?? '');
    if (address === null) {
      setValue('propertyAddressObject', null);
    }
    // Only re-run validation once the field is already showing an error, so a
    // selection clears it without errors appearing mid-typing.
    if (errors.propertyAddress) {
      trigger('propertyAddress');
    }
  };

  const handleClose = () => {
    reset(DEFAULTS);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      await postProperty({
        propertyLocation: 'australian',
        propertyDetails: {
          propertyAddress: values.propertyAddress,
          ...(values.propertyAddressObject && {
            propertyAddressObject: values.propertyAddressObject,
          }),
          propertyValue: Number(values.propertyValue),
          propertyUse: values.propertyUse,
          propertyRentFrequency: values.propertyRentFrequency,
          propertyRent: Number(values.propertyRent),
          isMortgageOnProperty: values.isMortgageOnProperty,
          mortgageLender: values.mortgageLender,
          mortgageLoanLimit: Number(values.mortgageLoanLimit),
          propertyOwner: values.propertyOwner,
          mortgageInterest: Number(values.mortgageInterest),
        },
      }).unwrap();
      displaySuccess('Property added');
      reset(DEFAULTS);
      onClose();
    } catch (err) {
      displayError(err);
    }
  });

  return (
    <Modal
      title="Add a property"
      open={open}
      onCancel={handleClose}
      destroyOnClose
      width={720}
      footer={[
        <Button key="cancel" onClick={handleClose} htmlType="button">
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isLoading}
          onClick={() => onSubmit()}
          htmlType="button"
        >
          Add property
        </Button>,
      ]}
    >
      <Row gutter={[16, 12]}>
        <Col xs={24}>
          <GoogleCustomLocationField
            label="Property address"
            name="propertyAddress"
            control={control}
            placeholder="Search address..."
            location="australia"
            required
            error={errors.propertyAddress?.message}
            onSelectedAddressChange={handleSelectedAddressChange}
            setPropertyDetailsObject={(details) =>
              setValue('propertyAddressObject', details ?? null)
            }
          />
        </Col>
        <Col xs={24} md={12}>
          <SelectField
            label="Property owner"
            name="propertyOwner"
            control={control}
            options={TAX_PROPERTY_OWNER_TYPE_OPTIONS}
            placeholder="Select owner"
            required
            error={errors.propertyOwner?.message}
          />
        </Col>
        <Col xs={24} md={12}>
          <InputField
            label="Property value"
            name="propertyValue"
            control={control}
            type="number"
            placeholder="e.g. 850000"
            prefix="A$"
            required
            error={errors.propertyValue?.message}
          />
        </Col>

        <Col xs={24} md={12}>
          <SelectField
            label="Property use"
            name="propertyUse"
            control={control}
            options={PROPERTY_USE_TYPE_OPTIONS}
            placeholder="Select use"
          />
        </Col>

        <Col xs={24} md={12}>
          <SelectField
            label="Rental frequency"
            name="propertyRentFrequency"
            control={control}
            options={FREQUENCY_OPTIONS}
            placeholder="Select frequency"
          />
        </Col>

        <Col xs={24} md={12}>
          <InputField
            label="Rental income"
            name="propertyRent"
            control={control}
            type="number"
            placeholder="e.g. 2400"
            prefix="A$"
          />
        </Col>

        <Col xs={24} md={12}>
          <RadioGroupField
            label="Mortgage on this property?"
            name="isMortgageOnProperty"
            control={control}
            options={YES_NO}
          />
        </Col>

        {isMortgaged === true && (
          <>
            <Col xs={24} md={12}>
              <SelectField
                label="Lender"
                name="mortgageLender"
                control={control}
                options={PROPERTY_LENDER_OPTIONS}
                placeholder="Select lender"
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                label="Loan limit"
                name="mortgageLoanLimit"
                control={control}
                type="number"
                placeholder="e.g. 420000"
                prefix="A$"
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                label="Interest rate"
                name="mortgageInterest"
                control={control}
                type="number"
                placeholder="e.g. 5.55"
                suffix="%"
                allowDecimal
              />
            </Col>
          </>
        )}
      </Row>
    </Modal>
  );
};

export default AddPropertyModal;
